import type { VerifiedResultPayload } from '@/types';
import { formatDate, formatDateTime } from '@/utils/format';

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <span className="text-xs font-medium uppercase tracking-wide text-navy-400">{label}</span>
      <span className="text-sm font-medium text-navy-900 sm:text-right">{value || '—'}</span>
    </div>
  );
}

export default function ResultVerified({
  data,
  onReset,
}: {
  data: VerifiedResultPayload;
  onReset: () => void;
}) {
  const isResult = Boolean(data.resultCode);
  const kind = isResult ? 'Statement of Result' : 'certificate';

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-8 ring-emerald-50/60">
          <i className="ri-check-line text-3xl" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-emerald-700">{kind.toUpperCase()} VERIFIED</h2>
          <p className="mt-1 text-sm text-navy-500">
            This {kind} has been verified against the records of the issuing institution.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-navy-100 bg-navy-50/40">
        <div className="divide-y divide-navy-100 px-5">
          <Row label="Institution" value={data.institution} />
          <Row label="Student Name" value={data.studentName} />
          <Row label="Matriculation Number" value={data.matriculationNumber} />
          {isResult ? (
            <>
              <Row label="Programme" value={data.programme ?? ''} />
              <Row label="Degree" value={data.degree} />
              <Row label="Session" value={data.graduationSession} />
              <Row label="Class of Degree" value={data.classOfDegree ?? ''} />
              <Row label="CGPA" value={data.cgpa ?? ''} />
              <Row label="Result Verification Code" value={data.resultCode ?? ''} />
              <Row label="Issued" value={data.resultIssueDate ? formatDate(data.resultIssueDate) : ''} />
            </>
          ) : (
            <>
              <Row label="Faculty" value={data.faculty} />
              <Row label="Department" value={data.department} />
              <Row label="Degree" value={data.degree} />
              <Row label="Graduation Session" value={data.graduationSession} />
              <Row label="Certificate Code" value={data.certificateCode} />
            </>
          )}
          <Row label="Verification ID" value={data.verificationId} />
          <Row label="Verification Date" value={formatDateTime(data.verificationDate)} />
        </div>
      </div>

      <button
        onClick={onReset}
        className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-navy-200 bg-white px-4 py-2.5 text-sm font-medium text-navy-800 transition hover:bg-navy-50 cursor-pointer"
      >
        <i className="ri-refresh-line" />
        Verify Another Document
      </button>
    </div>
  );
}